import React from 'react';
import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';
import { isContactScreen } from 'components/CallScribe/contactScreen';
import {
  RECORDING_ACTIVE_LABEL,
  RECORDING_STOPPED_LABEL,
  STOPPING_RECORDING_LABEL,
  STOP_FAILED_TEXT,
  STOP_RECORDING_LABEL,
  STOP_RETRY_LABEL,
  type TRecordingControlState,
} from 'components/CallScribe/recordingStop';
import { normalizeExternalTicketId } from 'components/CallScribe/ticketId';

const useCallTopLineStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    topLine: {
      flex: '0 0 auto',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1),
      minHeight: 28,
      minWidth: 0,
    },
    recording: {
      display: 'inline-flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 28,
      padding: '0 4px 0 12px',
      borderRadius: 14,
      background: t.glass,
      boxShadow: `inset 0 0 0 1px ${t.glassBorder}`,
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.onGround,
      whiteSpace: 'nowrap',
    },
    chipStatic: {
      paddingRight: 12,
      color: t.onGroundMuted,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: t.stageEnd,
      flex: '0 0 auto',
    },
    dotStopped: {
      background: 'transparent',
      boxShadow: `inset 0 0 0 1.5px ${t.onGroundFaint}`,
    },
    stop: {
      position: 'relative',
      height: 24,
      padding: '0 8px',
      marginLeft: 2,
      border: 'none',
      borderRadius: 12,
      background: 'transparent',
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.heavy,
      color: t.stageInfo,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: '-8px -4px',
      },
      '&:hover:not(:disabled)': { background: alpha(t.stageInfo, 0.16) },
      '&:focus-visible': { outline: `2px solid ${t.stageFocus}`, outlineOffset: 2 },
      '&:disabled': { cursor: 'default', color: t.onGroundMuted },
    },
    error: {
      flexBasis: '100%',
      ...CS_FONT.caption,
      color: t.onGround,
    },
    ticket: {
      marginLeft: 'auto',
      ...CS_FONT.caption,
      fontWeight: 600,
      color: t.onGroundFaint,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    },
  };
});

export interface ICallTopLineProps {
  recordingState: TRecordingControlState;
  onStopRecording?: () => void;
}

const RecordingChip: React.FC<ICallTopLineProps> = ({ recordingState, onStopRecording }) => {
  const classes = useCallTopLineStyles();
  if (recordingState === 'hidden') return null;
  if (recordingState === 'stopped') {
    return (
      <span className={`${classes.chip} ${classes.chipStatic}`}>
        <span className={`${classes.dot} ${classes.dotStopped}`} aria-hidden />
        {RECORDING_STOPPED_LABEL}
      </span>
    );
  }
  const busy = recordingState === 'stopping';
  const failed = recordingState === 'failed';
  return (
    <>
      <span className={classes.chip}>
        <span className={classes.dot} aria-hidden />
        {RECORDING_ACTIVE_LABEL}
        <button
          type="button"
          className={classes.stop}
          onClick={onStopRecording}
          disabled={busy || !onStopRecording}
          aria-busy={busy}
          aria-label={failed ? STOP_RETRY_LABEL : STOP_RECORDING_LABEL}
        >
          {(() => {
            if (busy) return STOPPING_RECORDING_LABEL;
            return failed ? STOP_RETRY_LABEL : 'Stop';
          })()}
        </button>
      </span>
      {failed ? (
        <span className={classes.error} role="alert">
          {STOP_FAILED_TEXT}
        </span>
      ) : null}
    </>
  );
};

const CallTopLine: React.FC<ICallTopLineProps> = ({ recordingState, onStopRecording }) => {
  const classes = useCallTopLineStyles();
  const config = useAppSelector(ConfigurationSelector);
  const ticket = isContactScreen(config) ? null : normalizeExternalTicketId(config?.ticketId);

  return (
    <div className={classes.topLine}>
      <span className={classes.recording} role="status">
        <RecordingChip recordingState={recordingState} onStopRecording={onStopRecording} />
      </span>
      {ticket ? <span className={classes.ticket}>{`Ticket #${ticket}`}</span> : null}
    </div>
  );
};

export default CallTopLine;
